import React, { useEffect, useState, useMemo } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ShieldCheck, User, Search, KeyRound, Power, MailCheck, MailX, Edit3, History, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  credit_specialist: "Credit Specialist",
  team_member: "Team Member",
  client: "Client",
};

const ROLE_COLORS = {
  super_admin: "bg-red-100 text-red-700",
  admin: "bg-amber-100 text-amber-800",
  credit_specialist: "bg-indigo-100 text-indigo-700",
  team_member: "bg-sky-100 text-sky-800",
  client: "bg-slate-100 text-slate-700",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);

  const load = async () => {
    const params = { sort_by: sortBy, sort_dir: sortDir };
    if (q) params.q = q;
    if (roleFilter !== "all") params.role = roleFilter;
    if (statusFilter === "active") params.is_active = true;
    if (statusFilter === "disabled") params.is_active = false;
    if (statusFilter === "verified") params.is_verified = true;
    if (statusFilter === "unverified") params.is_verified = false;
    const { data } = await api.get("/admin/users", { params });
    setUsers(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter, statusFilter, sortBy, sortDir]);

  const isSuperAdmin = me?.role === "super_admin";

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.is_active !== false).length;
    const unverified = users.filter((u) => u.is_verified === false).length;
    const staff = users.filter((u) => u.role !== "client").length;
    return { total, active, unverified, staff };
  }, [users]);

  const toggleActive = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/toggle-active`);
      toast.success(u.is_active === false ? "Reactivated" : "Disabled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleVerified = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/toggle-verified`);
      toast.success(u.is_verified ? "Unverified" : "Marked verified");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      toast.success(`Temporary password: ${data.temporary_password}`, { duration: 15000 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resendWelcome = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resend-welcome`);
      const parts = [];
      parts.push(`Temp password: ${data.temporary_password}`);
      if (data.close?.ok) parts.push("· Close synced");
      if (data.email_delivery?.sent) parts.push("· Email sent");
      else if (data.email_delivery?.error) parts.push("· Email failed");
      if (data.sms_delivery?.sent) parts.push("· SMS sent");
      else if (data.sms_delivery?.skipped) parts.push("· SMS skipped");
      toast.success(parts.join(" "), { duration: 15000 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-6 cq-in flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Access Control</p>
          <h1 className="font-display text-4xl font-black text-slate-900">Team & Users</h1>
          <p className="text-slate-500 mt-1">Manage every account. {isSuperAdmin ? "You have full super-admin control." : "You can manage non-admin accounts."}</p>
        </div>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 rounded-xl" data-testid="add-user-btn"><Plus size={18} className="mr-1" /> New user</Button>
          </DialogTrigger>
          <AddUserDialog onCreated={() => { setOpenAdd(false); load(); }} isSuperAdmin={isSuperAdmin} />
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total users" value={stats.total} testid="stat-total" />
        <StatCard label="Active" value={stats.active} testid="stat-active" />
        <StatCard label="Unverified" value={stats.unverified} testid="stat-unverified" />
        <StatCard label="Staff" value={stats.staff} testid="stat-staff" />
      </div>

      <Card className="p-4 sm:p-6 rounded-2xl border-slate-100">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input placeholder="Search name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-10 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="users-search" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-48 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="users-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="users-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="unverified">Unverified</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="h-11 rounded-xl" data-testid="users-search-btn">Search</Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer" onClick={() => { setSortBy("name"); setSortDir(sortDir === "asc" ? "desc" : "asc"); }}>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8" data-testid="no-users">No users match.</TableCell></TableRow>}
              {users.map((u) => {
                const targetsAdmin = ["super_admin", "admin"].includes(u.role);
                const cannotManage = targetsAdmin && !isSuperAdmin;
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${targetsAdmin ? "bg-amber-100 text-amber-700" : u.role === "client" ? "bg-slate-100 text-slate-600" : "bg-sky-100 text-sky-700"}`}>
                          {targetsAdmin ? <ShieldCheck size={16} /> : <User size={16} />}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</p>
                          <p className="text-xs text-slate-500">{u.phone || "no phone"}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell><Badge className={`${ROLE_COLORS[u.role] || "bg-slate-100"} rounded-full`}>{ROLE_LABEL[u.role] || u.role}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline" className={u.is_active === false ? "text-red-600 border-red-200" : "text-green-700 border-green-200"}>
                          {u.is_active === false ? "Disabled" : "Active"}
                        </Badge>
                        <Badge variant="outline" className={u.is_verified === false ? "text-amber-700 border-amber-200" : "text-slate-600"}>
                          {u.is_verified === false ? "Unverified" : "Verified"}
                        </Badge>
                        {u.role === "client" && u.must_change_password && (
                          <Badge variant="outline" className="text-orange-700 border-orange-200 bg-orange-50" data-testid={`pw-pending-${u.id}`}>
                            <AlertCircle size={11} className="mr-0.5" /> Temp pw
                          </Badge>
                        )}
                        {u.role === "client" && u.must_change_password === false && (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50" data-testid={`pw-set-${u.id}`}>
                            Pw changed
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(u)} disabled={cannotManage} data-testid={`edit-user-${u.id}`}><Edit3 size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setHistoryUser(u)} data-testid={`history-user-${u.id}`}><History size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => resetPw(u)} disabled={cannotManage} data-testid={`reset-pw-${u.id}`} title="Reset password"><KeyRound size={16} /></Button>
                        {u.role === "client" && (
                          <Button size="icon" variant="ghost" onClick={() => resendWelcome(u)} className="text-amber-600 hover:text-amber-700" data-testid={`resend-welcome-${u.id}`} title="Resend welcome credentials (regenerates temp pw + syncs to Close + emails/SMS)"><Send size={16} /></Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => toggleVerified(u)} data-testid={`toggle-verified-${u.id}`}>{u.is_verified === false ? <MailX size={16} /> : <MailCheck size={16} />}</Button>
                        <Button size="icon" variant="ghost" onClick={() => toggleActive(u)} disabled={cannotManage || u.id === me?.id} className={u.is_active === false ? "text-green-600" : "text-red-600"} data-testid={`toggle-active-${u.id}`} title={u.is_active === false ? "Unlock account" : "Lock account"}><Power size={16} /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="text-red-600" disabled={cannotManage || u.id === me?.id} data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete user?</AlertDialogTitle><AlertDialogDescription>Removes {u.email}. Client records remain.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(u.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      {editing && <EditUserDialog user={editing} isSuperAdmin={isSuperAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {historyUser && <LoginHistoryDialog user={historyUser} onClose={() => setHistoryUser(null)} />}
    </AppShell>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">{label}</p>
      <p className="font-display text-3xl font-black mt-1 text-slate-900" data-testid={testid}>{value}</p>
    </Card>
  );
}

function AddUserDialog({ onCreated, isSuperAdmin }) {
  const [form, setForm] = useState({ name: "", email: "", role: "team_member", password: "TempPass123!" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/users", form); toast.success("User created"); onCreated(); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
      <DialogHeader><DialogTitle className="font-display text-2xl">New user</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-user-name" /></div>
        <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-user-email" /></div>
        <div>
          <Label>Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
              {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
              <SelectItem value="credit_specialist">Credit Specialist</SelectItem>
              <SelectItem value="team_member">Team Member</SelectItem>
              <SelectItem value="client">Client</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="new-user-password" /></div>
        <DialogFooter><Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="new-user-submit">{busy ? "Creating…" : "Create user"}</Button></DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditUserDialog({ user, isSuperAdmin, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: user.first_name || "", last_name: user.last_name || "",
    email: user.email, phone: user.phone || "", role: user.role,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/users/${user.id}`, form); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="edit-user-first" /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="edit-user-last" /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-user-email" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-user-phone" /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                <SelectItem value="credit_specialist">Credit Specialist</SelectItem>
                <SelectItem value="team_member">Team Member</SelectItem>
                <SelectItem value="client">Client</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="edit-user-save">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoginHistoryDialog({ user, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/users/${user.id}/login-history`).then(({ data }) => setEntries(data)).finally(() => setLoading(false));
  }, [user.id]);
  return (
    <Dialog open={!!user} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="login-history-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Login history — {user.email}</DialogTitle></DialogHeader>
        <div className="max-h-80 overflow-y-auto space-y-2">
          {loading && <p className="text-sm text-slate-400">Loading…</p>}
          {!loading && entries.length === 0 && <p className="text-sm text-slate-400" data-testid="no-history">No recorded logins yet.</p>}
          {entries.map((e) => (
            <div key={e.id} className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm">
              <p className="font-semibold text-slate-800">{new Date(e.created_at).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">IP: {e.ip || "—"} · UA: <span className="font-mono text-[10px] break-all">{(e.user_agent || "").slice(0, 90)}</span></p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
